import Cookies from "js-cookie";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import clsx from "clsx";

import { getApi } from "../../utils/api";
import { API_POLICY, API_GET_EMPLOYEE_FILE, API_POLICY_V2 } from "../../config/Endpoints";
import { displayFormatDate } from "../../utils/helpers";
import PolicyModal from "./PolicyModal";
import DocumentViewer from "../../components/DocumentViewer";
import axios from "axios";
import { FiEye } from "react-icons/fi";
import { Search } from "lucide-react";

import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../v2/components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import { Button } from "../../v2/components/ui/button/button.tsx";
import { TextField } from "../../v2/components/ui/text-field/text-field.tsx";
import { ResponsiveDataTable } from "../../v2/components/ui/responsive-table/responsive-table.tsx";
import { showError } from "../../v2/utils/toast.ts";

function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const msg = error?.response?.data?.message;
  if (typeof msg === "string" && msg.trim() !== "") return msg;
  if (typeof error?.message === "string" && error.message.trim() !== "")
    return error.message;
  return fallback;
}

const ModifyPolicy = ({ title, description }) => {
  const [policyList, setPolicyList] = useState([]);
  /** List fetch: start true so we never flash "no records" before the first request finishes. */
  const [isListLoading, setIsListLoading] = useState(true);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState();
  const [selectedPolicy, setSelectedPolicy] = useState({});
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [documentDetail, setDocumentDetail] = useState({
    url: null,
    type: null,
    name: null,
  });

  const accessToken = Cookies.get("accessToken");
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPolicy, setTotalPolicy] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "",
  });

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleViewFile = useCallback(
    async (fullPath, name) => {
      const filepath = fullPath.split("/").slice(3).join("/");
      setIsDocumentLoading(true);
      try {
        const res = await axios.get(API_GET_EMPLOYEE_FILE, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { filepath },
          responseType: "blob", // important for binary files
        });

        if (res.status === 200) {
          // Use Blob type as the correct mime type
          const fileBlob = new Blob([res.data], {
            type: res.data.type || "application/pdf",
          });
          const fileURL = window.URL.createObjectURL(fileBlob);

          setDocumentDetail({
            url: fileURL,
            type: "application/pdf",
            name: name || "Document",
          });
        }
      } catch (error) {
        console.error("Error fetching file:", error);
        showError(getApiErrorMessage(error, "Unable to open the document."));
      } finally {
        setIsDocumentLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    if (documentDetail.url) {
      const openModalButton = document.getElementById("open-document-view");
      if (openModalButton) openModalButton.click();
    }
  }, [documentDetail.url]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match legacy fetch on page/limit only
  }, [currentPage, limit]);

  const fetchList = () => {
    setIsListLoading(true);
    getApi(API_POLICY_V2 + `?pageSize=${limit}&page=${currentPage}`, accessToken)
      .then((res) => {
        if (res.status === 200) {
          const rows = res?.data?.data?.data;
          const list = Array.isArray(rows) ? rows : [];
          setPolicyList(list);
          setFilteredItems(list);
          setTotalPolicy(res?.data?.data?.totalPolicy ?? 0);
        }
      })
      .catch((e) => {
        console.log(e);
        showError(getApiErrorMessage(e, "Failed to load policies."));
      })
      .finally(() => {
        setIsListLoading(false);
      });
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    const filtered = policyList.filter((item) =>
      item.title.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredItems(filtered);
  };

  const handleViewDetails = (data) => {
    setSelectedPolicy(data);
    setPolicyModalOpen(true);
  };

  const openAddPolicyModal = () => {
    setSelectedPolicy({});
    setPolicyModalOpen(true);
  };

  const searchFilteredList = useMemo(
    () =>
      policyList.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [policyList, searchTerm],
  );

  const onTableSortChange = (nextKey, nextDir) => {
    if (!nextKey || nextDir == null) {
      setSortConfig({ key: null, direction: "" });
      setFilteredItems([...searchFilteredList]);
      return;
    }
    const direction = nextDir === "asc" ? "ascending" : "descending";
    setSortConfig({ key: nextKey, direction });
    const sortedData = [...searchFilteredList].sort((a, b) => {
      const key = nextKey;
      if (key === "validFrom") {
        const dateA = new Date(a[key]);
        const dateB = new Date(b[key]);
        if (dateA < dateB) {
          return direction === "ascending" ? -1 : 1;
        }
        if (dateA > dateB) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      } else {
        if (a[key] < b[key]) {
          return direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });
    setFilteredItems(sortedData);
  };

  const tableSortKey = sortConfig.key ?? undefined;
  const tableSortDir =
    sortConfig.direction === "ascending"
      ? "asc"
      : sortConfig.direction === "descending"
        ? "desc"
        : null;

  const tableRows = useMemo(
    () => (Array.isArray(filteredItems) ? filteredItems : []),
    [filteredItems],
  );

  const tableColumns = useMemo(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: "title",
        sortable: true,
        truncate: true,
        getTitleText: (row) => String(row.title ?? ""),
      },
      {
        id: "shiftType",
        header: "Region",
        accessor: "shiftType",
        sortable: true,
        getSortValue: (row) => String(row.shiftType ?? ""),
        render: (row) => row.shiftType ?? "-",
      },
      {
        id: "validFrom",
        header: "Valid From",
        accessor: "validFrom",
        sortable: true,
        getSortValue: (row) => new Date(row.validFrom).getTime(),
        render: (row) =>
          displayFormatDate(
            row.validFrom,
            isListLoading && tableRows.length > 0,
          ),
      },
      {
        id: "description",
        header: "Description",
        accessor: "description",
        sortable: true,
        truncate: true,
        getTitleText: (row) => String(row.description ?? ""),
      },
      {
        id: "document",
        header: "Document",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <Button
            type="button"
            variant="accent-ghost"
            size="sm"
            startIcon={<FiEye size={14} aria-hidden />}
            onClick={() =>
              handleViewFile(row.documentPath, row.documentName)
            }
          >
            View
          </Button>
        ),
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <Button
            type="button"
            variant="accent"
            size="sm"
            onClick={() => handleViewDetails(row)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [handleViewFile, isListLoading, tableRows.length],
  );

  return (
    <PageShell>
      {(isListLoading || isDocumentLoading) && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              visible={true}
            />
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          {title ? (
            <h1 className={clsx("heading-md", "mb-1")}>{title}</h1>
          ) : null}
          {description ? (
            <p className={clsx("body-sm", "text-muted", "mb-0")}>{description}</p>
          ) : null}
        </div>
      </div>


      <div className="row align-items-center g-2 g-md-3 mb-3 mx-0 w-100">
        <div className="col-12 col-md-8 col-lg-5 p-0">
          <TextField
            id="policy-list-search"
            type="text"
            name="search"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by policy title"
            startIcon={
              <Search size={18} strokeWidth={2} aria-hidden />
            }
          />
        </div>
        <div className="col-12 col-md-auto ms-md-auto d-grid d-md-inline">
          <Button type="button" variant="accent" onClick={openAddPolicyModal}>
            + Add Policy
          </Button>
        </div>
      </div>

      <ResponsiveDataTable
        columns={tableColumns}
        data={tableRows}
        aria-label="Policies"
        serialNumber
        sortKey={tableSortKey}
        sortDir={tableSortDir}
        onSortChange={onTableSortChange}
        page={currentPage}
        pageSize={limit}
        totalItems={totalPolicy}
        onPageChange={handlePageChange}
        onPageSizeChange={handleLimitChange}
        totalLabel={
          <span className="body-sm text-muted">
            Total Policies: <strong className="text-body">{totalPolicy}</strong>
          </span>
        }
        emptyState={
          isListLoading ? undefined : (
            <EmptyStateMessage message="No Record Available" />
          )
        }
      />


      <PolicyModal
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        selectedPolicy={selectedPolicy}
        setSelectedPolicy={setSelectedPolicy}
        fetchList={fetchList}
      />

      <button
        id="open-document-view"
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropDocumentViewer"
        style={{ display: "none" }}
      ></button>

      {documentDetail.url && (
        <DocumentViewer
          url={documentDetail.url}
          contentType={documentDetail.type}
          fileName={documentDetail.name}
        />
      )}
    </PageShell>
  );
};

export default ModifyPolicy;
